import { useMemo, useState } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";
import { cn } from "@/lib/utils";

const formSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().min(1, "Description is required"),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  dueDate: z.string().optional(),
  entityType: z.enum(["client", "prospect"]),
  entityId: z.string().optional(),
  assignedTo: z.string().optional(),
  parentTaskId: z.number().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface CreateTaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentTask?: { id: number; taskNumber: string; clientId?: number | null } | null;
  onCreated?: () => void;
}

export function CreateTaskDialog({ open, onOpenChange, parentTask, onCreated }: CreateTaskDialogProps) {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [entityPopoverOpen, setEntityPopoverOpen] = useState(false);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      description: "",
      priority: "medium",
      dueDate: "",
      entityType: "client",
      entityId: "",
      assignedTo: "",
    },
  });

  const entityType = form.watch("entityType");
  const entityId = form.watch("entityId");

  // Queries
  const { data: clients = [] } = useQuery<any[]>({ queryKey: ["/api/clients"] });
  const { data: prospects = [] } = useQuery<any[]>({ queryKey: ["/api/prospects"] });

  const sortedClients = useMemo(
    () => [...clients].sort((a: any, b: any) => (a.name || "").localeCompare(b.name || "")),
    [clients]
  );
  const sortedProspects = useMemo(
    () => [...prospects].sort((a: any, b: any) => ((a.name || a.companyName || "").localeCompare(b.name || b.companyName || ""))),
    [prospects]
  );
  const entityList = entityType === "client" ? sortedClients : sortedProspects;

  const selectedEntity = entityId ? entityList.find((e: any) => String(e.id) === entityId) : null;
  const selectedTeamId = (selectedEntity as any)?.teamId;

  const { data: teamMembersData } = useQuery({
    queryKey: ["tasks", "team-members", selectedTeamId],
    queryFn: async () => {
      const url = selectedTeamId
        ? `/api/tasks/team-members?teamId=${selectedTeamId}`
        : "/api/tasks/team-members";
      const res = await apiRequest("GET", url);
      return res.json();
    },
  });
  const teamMembers = teamMembersData?.teamMembers || teamMembersData || [];

  const createMutation = useMutation({
    mutationFn: async (data: FormValues) => {
      const requesterName = user?.firstName && user?.lastName
        ? `${user.firstName} ${user.lastName}`
        : user?.email || "Broker";
      return apiRequest("POST", "/api/tasks", {
        title: data.title,
        description: data.description,
        priority: data.priority,
        dueDate: data.dueDate || undefined,
        category: "general",
        requesterName,
        requesterEmail: user?.email || "",
        requesterType: "broker",
        requesterId: user?.id,
        clientId: data.entityType === "client" && data.entityId ? parseInt(data.entityId) : undefined,
        prospectId: data.entityType === "prospect" && data.entityId ? parseInt(data.entityId) : undefined,
        assignedTo: data.assignedTo || undefined,
        parentTaskId: parentTask?.id || undefined,
        tags: [],
        source: "manual",
        createdBy: user?.id,
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["tasks"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["tickets"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["tickets-slideout"], exact: false });
      await queryClient.invalidateQueries({ queryKey: ["/api/tasks/stats"] });
      onOpenChange(false);
      form.reset();
      onCreated?.();
      toast({ title: "Success", description: "Task created successfully", variant: "success" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to create task", variant: "destructive" });
    },
  });

  const onSubmit = (data: FormValues) => {
    createMutation.mutate(data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {parentTask ? `Create Subtask of ${parentTask.taskNumber}` : "Create New Task"}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
            {/* Section 1: Task */}
            <div className="space-y-3">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Brief description of the issue" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Detailed description of the issue or request" rows={3} {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="priority"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Priority</FormLabel>
                      <Select value={field.value} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="low">Low</SelectItem>
                          <SelectItem value="medium">Medium</SelectItem>
                          <SelectItem value="high">High</SelectItem>
                          <SelectItem value="urgent">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="dueDate"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Due Date</FormLabel>
                      <FormControl>
                        <Input type="date" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Section 2: Link to */}
            <div className="rounded-lg border p-3 space-y-3">
              <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Link to</div>
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="entityType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type</FormLabel>
                      <Select
                        value={field.value}
                        onValueChange={(v: "client" | "prospect") => {
                          field.onChange(v);
                          form.setValue("entityId", "");
                          form.setValue("assignedTo", "");
                        }}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="client">Client</SelectItem>
                          <SelectItem value="prospect">Prospect</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="entityId"
                  render={({ field }) => {
                    const selectedName = entityList.find((e: any) => String(e.id) === field.value)?.name
                      || entityList.find((e: any) => String(e.id) === field.value)?.companyName;
                    return (
                      <FormItem>
                        <FormLabel>{entityType === "client" ? "Client" : "Prospect"}</FormLabel>
                        <Popover open={entityPopoverOpen} onOpenChange={setEntityPopoverOpen}>
                          <PopoverTrigger asChild>
                            <FormControl>
                              <Button
                                variant="outline"
                                role="combobox"
                                className={cn("w-full justify-between font-normal", !field.value && "text-muted-foreground")}
                              >
                                {selectedName || `Select ${entityType}`}
                                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                              </Button>
                            </FormControl>
                          </PopoverTrigger>
                          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" onWheel={(e) => e.stopPropagation()}>
                            <Command>
                              <CommandInput placeholder={`Search ${entityType}s...`} />
                              <CommandEmpty>No {entityType} found.</CommandEmpty>
                              <CommandList style={{ maxHeight: "250px", overflowY: "auto" }}>
                                <CommandGroup>
                                  <CommandItem
                                    value="__none__"
                                    onSelect={() => {
                                      field.onChange("");
                                      form.setValue("assignedTo", "");
                                      setEntityPopoverOpen(false);
                                    }}
                                  >
                                    <Check className={cn("mr-2 h-4 w-4", !field.value ? "opacity-100" : "opacity-0")} />
                                    None
                                  </CommandItem>
                                  {entityList.map((e: any) => (
                                    <CommandItem
                                      key={e.id}
                                      value={e.name || e.companyName || ""}
                                      onSelect={() => {
                                        field.onChange(String(e.id));
                                        form.setValue("assignedTo", "");
                                        setEntityPopoverOpen(false);
                                      }}
                                    >
                                      <Check className={cn("mr-2 h-4 w-4", field.value === String(e.id) ? "opacity-100" : "opacity-0")} />
                                      {e.name || e.companyName}
                                    </CommandItem>
                                  ))}
                                </CommandGroup>
                              </CommandList>
                            </Command>
                          </PopoverContent>
                        </Popover>
                        <FormMessage />
                      </FormItem>
                    );
                  }}
                />
              </div>
            </div>

            {/* Section 3: Assignment */}
            <div className="rounded-lg border p-3 space-y-3">
              <FormField
                control={form.control}
                name="assignedTo"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Assign To</FormLabel>
                    <Select
                      value={field.value || "unassigned"}
                      onValueChange={(v) => field.onChange(v === "unassigned" ? "" : v)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select team member" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="unassigned">Unassigned</SelectItem>
                        {teamMembers.map((member: any) => (
                          <SelectItem key={member.id} value={String(member.id)}>
                            {member.firstName} {member.lastName}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={createMutation.isPending}>
                {createMutation.isPending ? "Creating..." : "Create Task"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
